import { JoinedMeetingEvent } from '../events/JoinedMeetingEvent'
import { MeetingEndedEvent } from '../events/MeetingEndedEvent'
import { MeetingHeatMap } from './MeetingHeatMap'

// Count the hosts/co-hosts in a Zoom attendee list (a getAttendeeslist result). The
// SDK's participant items expose isHost / isCoHost booleans. Exported so the counting
// rule is unit-tested without the Zoom globals.
export function countHosts (attendees) {
    if (!Array.isArray(attendees)) {
        return 0
    }

    return attendees.filter(attendee => attendee && (attendee.isHost || attendee.isCoHost)).length
}

export class ZoomMtgController {
    #meeting
    #config = {}
    #leaveUrl
    #isHost
    #isTeacher

    // Live count of hosts/co-hosts in the meeting, seeded on join and refreshed on every
    // join/leave, so a disconnect can tell whether the leaving user is the LAST host.
    #hostCount = 0

    #meetingHeatMap = MeetingHeatMap.getInstance()

    constructor (leaveUrl) {
        this.#meeting = window.ZoomMtg
        this.#leaveUrl = leaveUrl
    }

    authorizeForHost (isTeacher = true) {
        this.#isHost = true
        this.#isTeacher = isTeacher

        this.#config = {
            disablePictureInPicture: true,
            isSupportAV: true,
            leaveUrl: this.#leaveUrl
        }
    }

    authorizeForAttendee () {
        this.#isHost = false

        this.#config = {
            debug: false, // optional
            audioPanelAlwaysOpen: true, // optional
            disableCallOut: true, // optional
            // disableCORP: true, // optional
            disableInvite: true, // optional
            disableJoinAudio: false, // optional
            disablePictureInPicture: true,
            disablePreview: true, // optional
            disableRecord: true, // optional
            disableReport: true, // optional
            disableVoIP: false, // optional
            enableWaitingRoomPreview: false,
            // inviteUrlFormat: '', // optional
            // isLockBottom: true, // optional,
            // isShowJoiningErrorDialog: true, // optional,
            isSupportAV: true, // optional,
            // isSupportBreakout: true, //optional
            // isSupportCC: false, //optional,
            isSupportNonverbal: false, // optional,
            isSupportPolling: true, // optional
            // isSupportQA: true, //optional,
            // leaveUrl: 'http://www.zoom.us', //required
            leaveUrl: this.#leaveUrl,
            // loginWindow: {  // optional,
            //     width: 400,
            //     height: 380
            // },
            // meetingInfo: [ // optional
            //     'topic',
            //     'host',
            //     'mn',
            //     'pwd',
            //     'telPwd',
            //     'invite',
            //     'participant',
            //     'dc',
            //     'enctype',
            //     'report'
            // ],
            // rwcBackup: '', //optional,
            screenShare: false, // optional,
            // sharingMode: 'both', //optional,
            showMeetingHeader: false, // option
            showPureSharingContent: true, // optional
            videoDrag: false, // optional,
            videoHeader: false // optional,
            // webEndpoint: 'PSO web domain', // PSO option
        }
    }

    initMeeting (configs = {}) {
        this.#showUI()

        this.#meeting.init({
            ...this.#config,
            success: () => {
                this.joinMeeting(configs)
            },
            error: (error) => {
                console.log(error)
            }
        })

        this.#meeting.inMeetingServiceListener('onUserJoin', () => {
            this.#meetingHeatMap.increaseUserCount()
            this.#refreshHostCount()
        })

        this.#meeting.inMeetingServiceListener('onUserLeave', () => {
            this.#meetingHeatMap.decreaseUserCount()
            this.#refreshHostCount()
        })

        window.ZoomMtg.inMeetingServiceListener('onMeetingStatus', (data) => {
            // {status: 1(connecting), 2(connected), 3(disconnected), 4(reconnecting)}
            // console.log('inMeetingServiceListener onMeetingStatus', data)
            // 2 => meeting baslatildi
            if (data?.meetingStatus === 2) {
                // http requestte başka bir sorun olabileceği gerekçesiyle sorgudan önce event fırlatmak
                window.top.postMessage('meetingConnected', '*')

                window.top.postMessage('meetingConnectedRequest', '*')
            }

            // 3 => disconnected: bu client toplantıdan koptu. SDK bunu "host herkes için
            // bitirdi" ile "ben sadece ayrıldım" arasında AYIRMAZ (ikisi de yerel status 3),
            // o yüzden dersi ancak SON host ayrılıyorsa bitiriyoruz: canlı host sayısı ≤ 1
            // (yalnızca ben) ve ben host'sam. Birden fazla host varken biri ayrılınca
            // #hostCount > 1 kaldığı için event fırlamaz; diğerleri derste kalır.
            if (data?.meetingStatus === 3 && this.#isHost && this.#hostCount <= 1) {
                document.dispatchEvent(new MeetingEndedEvent())
            }
        })
    }

    // Cache the current host/co-host count from getAttendeeslist (async; can't be read
    // once we've disconnected, so we keep the latest value for the status-3 decision).
    #refreshHostCount () {
        this.#meeting.getAttendeeslist({
            success: (res) => {
                const attendees = Array.isArray(res) ? res : ((res && res.result) || [])
                this.#hostCount = countHosts(attendees)
            }
        })
    }

    #showUI () {
        // todo buraya neden ihtiyac duyduk?
        // ZoomClient.vue component icerisinde css ile ilk basta display none yapilmis
        document.getElementById('zmmtg-root').style.display = 'block'
    }

    joinMeeting (configs = {}) {
        this.#meeting.join({
            ...configs,
            success: () => {
                /* if (!this.#isHost) {
                    this.#meeting.showJoinAudioFunction({ show: false })
                } */

                document.dispatchEvent(new JoinedMeetingEvent())

                // Seed the host count immediately on join (don't wait for the next
                // join/leave), so a solo host who leaves right away is still detected.
                this.#refreshHostCount()

                this.configureCustomUI()
            },
            error: (error) => {
                console.log(error)
            }
        })
    }

    configureCustomUI () {
        if (this.#isHost) {
            this.setHtmlClassesForHost()
            return
        }

        this.setChatConfig()

        const activateAudioTimout = setTimeout(() => {
            this.activateAudio()
            clearInterval(activateAudioTimout)
        }, 5000)
    }

    setHtmlClassesForHost () {
        const htmlElement = document.querySelector('html')
        const htmlElementClasses = htmlElement.classList

        htmlElementClasses.add('meeting-host')

        if (this.#isTeacher) {
            htmlElementClasses.add('meeting-teacher')
        }
    }

    setChatConfig () {
        let isChatOpened = false

        const setChatConfigInterval = setInterval(() => {
            const chatButton = document.querySelector("[aria-label*='chat']")

            const onChatOpenedForChatButton = () => {
                const onChatOpenedForChatButtonInterval = setInterval(() => {
                    if (document.querySelector('.chat-container.window-content-bottom')) {
                        isChatOpened = true
                        chatButton.removeEventListener('click', onChatOpenedForChatButton)
                        clearInterval(onChatOpenedForChatButtonInterval)
                    }
                }, 1000)
            }

            if (!isChatOpened && chatButton) {
                chatButton.addEventListener('click', onChatOpenedForChatButton)
                chatButton.click()
            }

            if (document.querySelector('#chatReceiverMenu')) {
                const chatReceiverMenu = document.querySelector('#chatReceiverMenu')
                const receiverName = chatReceiverMenu.querySelector('.chat-receiver-list__receiverName')
                if (receiverName) {
                    receiverName.click()
                }

                const menuItems = chatReceiverMenu.querySelectorAll('.chat-receiver-list__menu-item')

                menuItems.forEach(item => {
                    // Eğer item içinde chat-receiver-list__appendix yoksa, elementi gizle
                    // Sadece oturum sahibinde chat-receiver-list__appendix var.
                    if (!item.querySelector('.chat-receiver-list__appendix')) {
                        item.style.display = 'none'
                    } else {
                        // oturum sahibini secelim chat yapilabileceklerden
                        item.click()
                    }
                })
                if (menuItems.length > 0) {
                    clearInterval(setChatConfigInterval)
                }
            }
        }, 1000)
    }

    activateAudio () {
        const joinAudioInterval = setInterval(() => {
            const joinAudioBtn = document.querySelector('.join-dialog .join-audio-by-voip__join-btn')
            if (joinAudioBtn) {
                // 'unmute my microphone' 'mute my microphone' 'join audio'
                joinAudioBtn.click()
                clearInterval(joinAudioInterval)
            }
        }, 1000)
    }
}
